import {
  Box,
  Button,
  Icon,
  PlainLink,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Scrollable,
  Tag,
  Text,
} from '@customerio/pluma-components/react';
import { AnimatePresence, m } from 'motion/react';
import plur from 'plur';
import { useState } from 'react';
import { useAnalytics } from '@/services/analytics';
import { isEmptyString } from '@/utils/string';
import * as styles from './index.css';

import { cleanReferenceTitle } from '../../utils/references';

export interface Reference {
  url: string;
  title: string;
  type?: 'business_profile';
}

interface ReferencesProps {
  references: Reference[];
  variant?: 'tabs' | 'scroll';
}

export function References({ references, variant = 'scroll' }: ReferencesProps) {
  const [_activeReferenceIndex, setActiveReferenceIndex] = useState<number>(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const { track } = useAnalytics();

  if (references.length === 0) {
    return null;
  }

  const cleanedReferences = references.reduce((accumulator, reference) => {
    const title = cleanReferenceTitle(reference.title);

    if (isEmptyString(title)) {
      return accumulator;
    }

    if (accumulator.some((r) => r.url === reference.url)) {
      return accumulator;
    }

    accumulator.push({
      ...reference,
      title,
    });

    return accumulator;
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  }, [] as Reference[]);

  const activeReferenceIndex = _activeReferenceIndex % cleanedReferences.length;
  const activeReference = cleanedReferences[activeReferenceIndex];

  const handleNavigation = (newDirection: 1 | -1, indexChange: (index: number) => number) => {
    if (isAnimating) return;

    const newIndex = indexChange(activeReferenceIndex);
    setDirection(newDirection);
    setActiveReferenceIndex(indexChange);

    track('aiAssistant', 'referencePaginated', {
      Direction: newDirection === 1 ? 'next' : 'previous',
      'Current Index': newIndex + 1,
      'Total References': cleanedReferences.length,
    });
  };

  return (
    <Popover withArrow={false} placement="bottom-start">
      <PopoverTrigger>
        <Tag cursor="pointer">
          {cleanedReferences.length} {plur('reference', cleanedReferences.length)}
        </Tag>
      </PopoverTrigger>
      <PopoverContent p="0" overflow="hidden" className={styles.referencesPopover}>
        {variant === 'tabs' ? (
          <>
            <Box backgroundColor="subtle" display="flex" justifyContent="space-between" alignItems="center" px="050">
              <Box>
                <Button
                  isIconOnly
                  icon="chevron-left"
                  onClick={() =>
                    handleNavigation(-1, (index) => (index === 0 ? cleanedReferences.length - 1 : index - 1))
                  }
                  aria-label="Previous reference"
                  size="sm"
                  variant="tertiary"
                  isDisabled={isAnimating}
                />
                <Button
                  isIconOnly
                  icon="chevron-right"
                  onClick={() =>
                    handleNavigation(1, (index) => (index === cleanedReferences.length - 1 ? 0 : index + 1))
                  }
                  aria-label="Next reference"
                  size="sm"
                  variant="tertiary"
                  isDisabled={isAnimating}
                />
              </Box>
              <Text size="xxs" color="subtle" p="100" display="block">
                {activeReferenceIndex + 1} of {cleanedReferences.length}
              </Text>
            </Box>
            <AnimatePresence mode="wait" custom={direction} initial={false}>
              <m.div
                key={activeReferenceIndex}
                custom={direction}
                initial={{ x: direction > 0 ? styles.REFERENCES_POPOVER_WIDTH : -styles.REFERENCES_POPOVER_WIDTH }}
                animate={{ x: 0 }}
                exit={{ x: direction > 0 ? -styles.REFERENCES_POPOVER_WIDTH : styles.REFERENCES_POPOVER_WIDTH }}
                transition={{ type: 'tween', ease: 'easeInOut', duration: 0.15 }}
                onAnimationStart={() => setIsAnimating(true)}
                onAnimationComplete={() => setIsAnimating(false)}
              >
                <PlainLink href={activeReference.url} isExternal className={styles.referenceLink}>
                  <Box flexGrow="1">
                    <Text truncate weight="medium" mb="050" size="sm">
                      {activeReference.title}
                    </Text>
                    <Text size="xxs" color="subtle" truncate>
                      {activeReference.url}
                    </Text>
                  </Box>
                  <Icon name="exit" size="sm" flexShrink="0" color="subtle" />
                </PlainLink>
              </m.div>
            </AnimatePresence>
          </>
        ) : (
          <>
            <Box backgroundColor="subtle" px="150" py="100">
              <Text size="xxs" color="subtle" weight="medium">
                {cleanedReferences.length} {plur('reference', cleanedReferences.length)}
              </Text>
            </Box>
            <Scrollable
              overflow="y"
              style={{ maxHeight: '256px' }}
              withEdgeIndicators={true}
              display="flex"
              flexDirection="column"
            >
              {cleanedReferences.map((reference) => (
                <PlainLink key={reference.url} href={reference.url} isExternal className={styles.referenceLink}>
                  <Box flexGrow="1">
                    <Text truncate weight="medium" mb="050" size="sm">
                      {reference.title}
                    </Text>
                    <Text size="xxs" color="subtle" truncate>
                      {reference.url}
                    </Text>
                  </Box>
                  <Icon name="exit" size="sm" flexShrink="0" color="subtle" />
                </PlainLink>
              ))}
            </Scrollable>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
